import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { cn } from '../lib/utils';
import { Link, Image } from './common/NextAdapters';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { TitleMeta, typeLabel } from './title-meta';
import { TitleCard } from './title-card';
import { MyListButton } from './my-list-button';
import { RatingButton } from './rating-button';
import { buildSeasons, formatEpisodeDuration } from '../lib/seasons';
import { deriveThumbnailFromVideoUrl } from '../lib/video-helper';
import { useAuthStore } from '../store';
import { useReactToMovie } from '../hooks/useMovie';
import { getApiErrorMessage } from '../api/errors';
import toast from 'react-hot-toast';

const SEASON_PILL_LIMIT = 6;

export function TitleDetailView({ title, cast = [], relatedTitles = [] }) {
  const navigate = useNavigate();
  const token = useAuthStore((state) => state.token);
  const reactionMutation = useReactToMovie();
  const [expandDescription, setExpandDescription] = useState(false);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [isMuted, setIsMuted] = useState(true);
  const [trailerReady, setTrailerReady] = useState(false);
  const [trailerError, setTrailerError] = useState(false);
  const heroVideoRef = useRef(null);

  const heroTrailerSrc = title?.trailer || title?.trailerUrl || null;

  // Same delayed-autoplay pattern as the homepage hero (HeroCarousel): sit on the poster
  // for a beat, then plain `<video src>` play — no hls.js, no stream proxy.
  useEffect(() => {
    if (!heroVideoRef.current || !heroTrailerSrc) return undefined;
    const timer = setTimeout(() => {
      if (!heroVideoRef.current) return;
      heroVideoRef.current.currentTime = 0;
      heroVideoRef.current
        .play()
        .then(() => setTrailerReady(true))
        .catch(() => setTrailerError(true));
    }, 5000);
    return () => clearTimeout(timer);
  }, [heroTrailerSrc]);

  useEffect(() => {
    if (heroVideoRef.current) heroVideoRef.current.muted = isMuted;
  }, [isMuted]);

  if (!title) {
    return (
      <div className="flex min-h-[70vh] items-center justify-center pt-20">
        <div className="text-center">
          <Icon
            icon="ph:film-slate"
            className="mx-auto size-16 text-muted-foreground/50"
          />
          <h1 className="display mt-6 text-2xl">Title not found</h1>
          <p className="mt-3 text-muted-foreground">
            This title may have been removed or is currently unavailable.
          </p>
          <Button asChild className="mt-6 rounded-full" variant="outline">
            <Link href="/">
              <Icon icon="ph:arrow-left-bold" className="mr-2 size-4" />
              Back to home
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const seasons = title.isSeries ? buildSeasons(title) : [];
  const currentSeason =
    seasons.find((season) => season.number === selectedSeason) ?? seasons[0] ?? null;
  const episodes = currentSeason?.episodes ?? [];

  // Determine watch / play target
  const firstEpisode = episodes[0] || (title.episodes && title.episodes[0]) || null;
  const playTargetEpisodeId = firstEpisode?.Id || firstEpisode?.id || title.id;

  // The player at /watch/:id is its own fixed, full-viewport cinema route — it doesn't need the
  // real browser Fullscreen API forced on beforehand (its own fullscreen button handles that).
  const handleWatchMain = () => {
    navigate(`/watch/${playTargetEpisodeId}`, { state: { autoplay: true } });
  };

  const handleMovieReaction = async (reaction) => {
    if (!token) {
      toast.error('Sign in to react to titles');
      return;
    }
    try {
      await reactionMutation.mutateAsync({
        movieId: Number(title.id),
        reaction: title.reactions?.CurrentUserReaction === reaction ? 'None' : reaction,
      });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save reaction'));
    }
  };

  const categoryNames = Array.isArray(title.categories) && title.categories.length > 0
    ? title.categories.map((c) => c.name).filter(Boolean)
    : [title.category].filter(Boolean);

  const metaItems = [
    typeLabel(title),
    title.year,
    categoryNames.join(' • '),
    title.duration,
    title.language,
    title.rating,
  ].filter(Boolean);

  return (
    <>
      {/* ── Hero section ────────────────────────────────────────────── */}
      <section className="relative isolate min-h-[58vh] sm:min-h-[64vh] lg:min-h-[70vh] flex items-end overflow-hidden -mt-16">
        {/* Background trailer video */}
        {heroTrailerSrc && !trailerError && (
          <video
            ref={heroVideoRef}
            src={heroTrailerSrc}
            muted={isMuted}
            playsInline
            loop
            preload="metadata"
            onError={() => setTrailerError(true)}
            className={cn(
              'absolute inset-0 z-0 size-full object-cover transition-opacity duration-1000',
              trailerReady ? 'opacity-100' : 'opacity-0'
            )}
          />
        )}

        {/* Background artwork */}
        {title.thumbnail ? (
          <Image
            src={title.thumbnail}
            alt=""
            fill
            priority
            sizes="100vw"
            className={cn(
              'object-cover object-top transition-opacity duration-1000',
              trailerReady ? 'opacity-0' : 'opacity-100'
            )}
          />
        ) : (
          <div className="absolute inset-0 bg-card" />
        )}

        {/* Scrims */}
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/65 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-44 sm:h-56 bg-gradient-to-t from-background via-background/85 to-transparent" />

        {/* Mute / Unmute toggle */}
        {heroTrailerSrc && trailerReady && (
          <button
            type="button"
            onClick={() => setIsMuted((m) => !m)}
            aria-label={isMuted ? 'Unmute trailer' : 'Mute trailer'}
            className="absolute right-6 bottom-12 z-30 flex size-11 items-center justify-center rounded-full border border-white/25 bg-black/50 text-white/80 backdrop-blur-sm transition-all duration-200 hover:border-white/40 hover:bg-black/70 hover:text-white sm:bottom-14 sm:right-10"
          >
            <Icon
              icon={
                isMuted
                  ? 'ph:speaker-simple-x-bold'
                  : 'ph:speaker-simple-high-bold'
              }
              className="size-5"
            />
          </button>
        )}

        {/* Content */}
        <div className="relative mx-auto w-full max-w-[1600px] gutter pb-8 pt-24 sm:pb-10 sm:pt-32 lg:pb-12">
          <div className="max-w-2xl">
            {/* Type badge */}
            <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-3.5 py-1 text-xs font-semibold tracking-wider text-white uppercase backdrop-blur-md">
              <span
                className={cn(
                  'size-1.5 rounded-full animate-pulse',
                  title.isSeries ? 'bg-violet' : 'bg-ember'
                )}
              />
              {title.isSeries ? 'TV Series' : 'Movie'}
            </div>

            {/* Title */}
            <h1 className="display mt-4 text-[clamp(2.5rem,6vw,4.5rem)] leading-[0.98] text-balance font-extrabold">
              {title.title}
            </h1>

            {/* Meta */}
            <div className="mt-3">
              <TitleMeta className="text-xs sm:text-sm" items={metaItems} />
            </div>

            {/* Description */}
            {title.description && (
              <div className="mt-4">
                <p
                  className={cn(
                    'max-w-[50ch] text-[15px] leading-relaxed text-foreground/80 sm:text-base transition-all duration-300',
                    !expandDescription && 'line-clamp-3'
                  )}
                >
                  {title.description}
                </p>
                {title.description.length > 200 && (
                  <button
                    type="button"
                    onClick={() => setExpandDescription(!expandDescription)}
                    className="mt-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {expandDescription ? 'Show less' : 'Read more'}
                  </button>
                )}
              </div>
            )}

            {/* Director */}
            {title.director && (
              <p className="mt-3 text-sm text-muted-foreground">
                <span className="text-foreground/60">Directed by</span>{' '}
                <span className="font-medium text-foreground/90">
                  {title.director}
                </span>
              </p>
            )}

            {/* Cast inline */}
            {title.cast && title.cast.length > 0 && (
              <p className="mt-1 text-sm text-muted-foreground">
                <span className="text-foreground/60">Starring</span>{' '}
                <span className="font-medium text-foreground/90">
                  {title.cast.slice(0, 4).join(', ')}
                </span>
              </p>
            )}

            {/* Action buttons */}
            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Button
                onClick={handleWatchMain}
                size="lg"
                className="h-12 rounded-full bg-white px-8 text-base font-semibold text-black transition-transform hover:scale-102 hover:bg-white/95"
              >
                <Icon icon="ph:play-fill" className="size-5 mr-2" />
                {title.isSeries ? 'Watch Now' : 'Play Movie'}
              </Button>

              {(title.trailer || title.trailerUrl) && (
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate(`/trailer/${title.id}`)}
                  className="h-12 rounded-full border-white/25 bg-white/10 px-6 text-base font-medium backdrop-blur-md hover:bg-white/20 hover:text-white"
                >
                  <Icon icon="ph:film-reel" className="size-5 mr-2" />
                  Trailer
                </Button>
              )}

              <MyListButton title={title} size="lg" />

              <RatingButton title={title} size="lg" />

              <Button
                variant="ghost"
                size="icon"
                aria-label={`Share ${title.title}`}
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({ title: title.title, url: window.location.href }).catch(() => {});
                  } else {
                    navigator.clipboard.writeText(window.location.href);
                  }
                }}
                className="size-12 rounded-full border border-white/25 bg-white/8 backdrop-blur-md hover:bg-white/15 text-white"
              >
                <Icon icon="ph:share-network-bold" className="size-5" />
              </Button>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2" aria-label="Rate this title">
              <ReactionButton
                label="Like"
                icon={<Icon icon="si:thumb-up-alt-1-fill" className="size-4" />}
                count={title.reactions?.Likes}
                selected={title.reactions?.CurrentUserReaction === 'Like'}
                disabled={reactionMutation.isPending}
                onClick={() => handleMovieReaction('Like')}
              />
              <ReactionButton
                label="Love It"
                icon={(
                  <span className="relative flex items-center">
                    <Icon icon="si:thumb-up-alt-1-fill" className="size-4" />
                    <Icon icon="si:thumb-up-alt-1-fill" className="-ml-1.5 size-4" />
                  </span>
                )}
                count={title.reactions?.DoubleLikes}
                selected={title.reactions?.CurrentUserReaction === 'DoubleLike'}
                disabled={reactionMutation.isPending}
                onClick={() => handleMovieReaction('DoubleLike')}
              />
              <ReactionButton
                label="Dislike"
                icon={<Icon icon="si:thumb-down-alt-1-fill" className="size-4" />}
                count={title.reactions?.Dislikes}
                selected={title.reactions?.CurrentUserReaction === 'Dislike'}
                disabled={reactionMutation.isPending}
                onClick={() => handleMovieReaction('Dislike')}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── Detail body ─────────────────────────────────────────────── */}
      <div className="relative z-10 w-full gutter pb-14">
        {/* ── Info pills row ───────────────────────────────────────── */}
        <div className="flex flex-wrap gap-2.5 border-b border-white/8 pb-6 pt-2">
          {title.year && (
            <InfoPill icon="ph:calendar-blank-bold" label={String(title.year)} />
          )}
          {categoryNames.map((name) => (
            <InfoPill key={name} icon="ph:tag-bold" label={name} />
          ))}
          {title.duration && (
            <InfoPill icon="ph:clock-bold" label={title.duration} />
          )}
          {title.language && (
            <InfoPill icon="ph:translate-bold" label={title.language} />
          )}
          {title.rating && (
            <InfoPill icon="ph:star-bold" label={title.rating} />
          )}
          {title.isSeries && title.episodeCount > 0 && (
            <InfoPill
              icon="ph:television-bold"
              label={`${title.episodeCount} episodes`}
            />
          )}
          {title.isComingSoon && (
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/25 bg-amber-500/10 px-4 py-2 text-sm font-medium text-amber-400">
              <Icon icon="ph:clock-countdown-bold" className="size-4" />
              Coming Soon
            </div>
          )}
        </div>

        {/* ── Seasons / Episodes (series only) ───────────────────────── */}
        {title.isSeries && currentSeason && (
          <section className="mt-10">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold tracking-tight display">Episodes</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {seasons.length > 1 ? `${seasons.length} seasons · ` : ''}
                  {episodes.length} {episodes.length === 1 ? 'episode' : 'episodes'} in{' '}
                  {currentSeason.label}
                </p>
              </div>
              <SeasonPicker
                seasons={seasons}
                current={currentSeason}
                onSelect={setSelectedSeason}
              />
            </div>

            <div className="mt-6 grid gap-3">
              {episodes.map((ep) => (
                <EpisodeCardItem
                  key={ep.Id || ep.id || `${ep.seasonNumber}-${ep.episodeNumber}`}
                  episode={ep}
                  titleId={title.id}
                  onWatch={(epId) => navigate(`/watch/${epId}`, { state: { autoplay: true } })}
                />
              ))}
              {episodes.length === 0 && (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No episodes available for this season yet.
                </p>
              )}
            </div>
          </section>
        )}

        {/* ── Cast grid ─────────────────────────────────────────────── */}
        {cast && cast.length > 0 && (
          <section className="mt-12">
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight display">Cast</h2>
            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {cast.map((member) => (
                <CastCardItem key={member.id || member.Id || member.name || member.Name} member={member} />
              ))}
            </div>
          </section>
        )}

        {/* ── Additional details ────────────────────────────────────── */}
        <section className="mt-12 border-t border-white/8 pt-8">
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight display">Details</h2>
          <div className="mt-6 grid grid-cols-1 gap-x-12 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
            {title.director && (
              <DetailItem label="Director" value={title.director} />
            )}
            {title.cast && title.cast.length > 0 && (
              <DetailItem label="Cast" value={title.cast.join(', ')} />
            )}
            {categoryNames.length > 0 && (
              <DetailItem label="Genre" value={categoryNames.join(' • ')} />
            )}
            {title.year && (
              <DetailItem label="Release Year" value={String(title.year)} />
            )}
            {title.language && (
              <DetailItem label="Language" value={title.language} />
            )}
            {title.contentType && (
              <DetailItem label="Type" value={title.contentType} />
            )}
          </div>
        </section>

        {/* ── Related titles ─────────────────────────────────────────── */}
        {relatedTitles && relatedTitles.length > 0 && (
          <section className="mt-14 border-t border-white/8 pt-10">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold tracking-tight display">
                  {title.isSeries ? 'Related Series' : 'Related Movies'}
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  {title.category
                    ? `More ${title.category} titles you might like`
                    : `Similar ${title.isSeries ? 'series' : 'movies'} to stream`}
                </p>
              </div>
              <Link
                href="/browse"
                className="group/all flex shrink-0 items-center gap-1 rounded-full border border-white/12 px-3.5 py-1.5 text-xs sm:text-sm text-muted-foreground transition-colors hover:border-white/30 hover:text-foreground"
              >
                Explore all
                <Icon
                  icon="ph:caret-right-bold"
                  className="size-3 transition-transform duration-200 group-hover/all:translate-x-0.5"
                />
              </Link>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
              {relatedTitles.slice(0, 12).map((item) => (
                <TitleCard key={item.id || item.Id} title={item} />
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}

function InfoPill({ icon, label }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/5 px-4 py-2 text-sm font-medium text-foreground/80 backdrop-blur-sm">
      <Icon icon={icon} className="size-4 text-muted-foreground" />
      {label}
    </div>
  );
}

function formatReactionCount(count) {
  const n = count || 0;
  if (n < 1000) return String(n);
  const scaled = Math.round(n / 100) / 10;
  return `${scaled % 1 === 0 ? scaled.toFixed(0) : scaled}k`;
}

function ReactionButton({ label, icon, count = 0, selected, disabled, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={`${label}: ${count || 0}`}
      aria-pressed={selected}
      className={cn(
        'inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-wait disabled:opacity-60',
        selected
          ? label === 'Dislike'
            ? 'border-destructive bg-destructive/20 text-white'
            : 'border-primary bg-primary/20 text-white'
          : 'border-white/15 bg-white/5 text-white/75 hover:border-white/30 hover:text-white'
      )}
    >
      {icon}
      <span>{label}</span>
      <span className="inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-white/15 px-1.5 py-0.5 text-xs font-semibold tabular-nums">
        {formatReactionCount(count)}
      </span>
    </button>
  );
}

function DetailItem({ label, value }) {
  return (
    <div>
      <dt className="text-xs font-semibold tracking-wider text-muted-foreground uppercase">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-foreground/90">{value}</dd>
    </div>
  );
}

function CastCardItem({ member }) {
  const name = member.name || member.Name || 'Cast Member';
  const photo = member.photo || member.PhotoUrl || member.ImageUrl;
  const character = member.character || member.Character || member.Role;
  const id = member.id || member.Id || member.ActorId;

  return (
    <Link
      href={id ? `/actor/${id}` : '#'}
      className="group/cast relative flex flex-col items-center rounded-xl border border-white/8 bg-white/[0.03] p-4 text-center transition-all duration-300 hover:border-white/15 hover:bg-white/[0.06]"
    >
      <div className="relative size-20 overflow-hidden rounded-full bg-card ring-2 ring-white/10 transition-transform duration-300 group-hover/cast:scale-105">
        {photo ? (
          <Image
            src={photo}
            alt={name}
            fill
            sizes="80px"
            className="object-cover"
          />
        ) : (
          <div className="flex size-full items-center justify-center bg-white/5 text-muted-foreground">
            <Icon icon="ph:user-bold" className="size-8" />
          </div>
        )}
      </div>
      <p className="mt-3 truncate text-sm font-medium text-foreground group-hover/cast:text-white">
        {name}
      </p>
      {character && (
        <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
          {character}
        </p>
      )}
    </Link>
  );
}

function SeasonPicker({ seasons, current, onSelect }) {
  if (seasons.length <= 1) {
    return (
      <span className="self-start rounded-full border border-white/12 px-4 py-1.5 text-sm font-medium text-muted-foreground sm:self-auto">
        {current.label}
      </span>
    );
  }

  if (seasons.length <= SEASON_PILL_LIMIT) {
    return (
      <div
        role="group"
        aria-label="Choose a season"
        className="no-scrollbar -mx-1 flex gap-1.5 overflow-x-auto px-1 py-1"
      >
        {seasons.map((season) => {
          const active = season.number === current.number;
          return (
            <button
              key={season.number}
              type="button"
              onClick={() => onSelect(season.number)}
              aria-pressed={active}
              className={cn(
                'whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200 focus-visible:outline-none',
                active
                  ? 'brand-sweep text-white shadow-md'
                  : 'border border-white/12 text-muted-foreground hover:border-white/25 hover:text-foreground'
              )}
            >
              {season.label}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`${current.label}. Choose a season`}
          className="flex h-10 items-center gap-2 self-start rounded-full border border-white/15 bg-white/5 pr-3 pl-4 text-sm font-semibold text-foreground transition hover:border-white/30 hover:bg-white/10 focus-visible:outline-none sm:self-auto"
        >
          <Icon icon="ph:stack-bold" className="size-4 text-muted-foreground" />
          {current.label}
          <Icon icon="ph:caret-down-bold" className="size-3.5 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-80 w-56 overflow-y-auto">
        {seasons.map((season) => {
          const active = season.number === current.number;
          return (
            <DropdownMenuItem
              key={season.number}
              onClick={() => onSelect(season.number)}
              className={cn('cursor-pointer', active && 'font-semibold text-primary')}
            >
              <span className="flex-1">{season.label}</span>
              <span className="text-xs text-muted-foreground">
                {season.episodes.length} {season.episodes.length === 1 ? 'ep' : 'eps'}
              </span>
              {active ? <Icon icon="ph:check-bold" className="size-3.5 text-primary ml-2" /> : null}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function EpisodeCardItem({ episode, onWatch }) {
  const index = episode.episodeNumber || episode.EpisodeNumber || 1;
  const thumbnail = episode.ThumbnailUrl || episode.thumbnailUrl || episode.Thumbnail
    || deriveThumbnailFromVideoUrl(episode.VideoUrl || episode.videoUrl);
  const duration = formatEpisodeDuration(episode);
  const title = episode.Title || episode.title || `Episode ${index}`;
  const description = episode.Description || episode.description;
  const episodeId = episode.Id || episode.id || index;

  return (
    <div className="group/ep flex flex-col gap-4 rounded-xl border border-white/8 bg-white/[0.02] p-3.5 transition-all duration-300 hover:border-white/15 hover:bg-white/[0.05] sm:flex-row sm:items-center sm:gap-5">
      <button
        type="button"
        onClick={() => onWatch(episodeId)}
        className="relative aspect-video w-full shrink-0 overflow-hidden rounded-lg bg-card sm:w-48 block group/thumb focus-visible:outline-none cursor-pointer text-left"
      >
        {thumbnail ? (
          <Image
            src={thumbnail}
            alt={title}
            fill
            sizes="(max-width: 640px) 100vw, 192px"
            className="object-cover transition-transform duration-300 group-hover/ep:scale-105"
          />
        ) : (
          <div className="flex size-full items-center justify-center bg-white/5 text-muted-foreground">
            <Icon icon="ph:film-slate" className="size-8" />
          </div>
        )}
        <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover/ep:opacity-100">
          <div className="flex size-10 items-center justify-center rounded-full bg-white text-black shadow-lg transition-transform duration-200 group-hover/ep:scale-110">
            <Icon icon="ph:play-fill" className="ml-0.5 size-5" />
          </div>
        </div>
      </button>

      <div className="flex min-w-0 flex-1 flex-col justify-center">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-muted-foreground">E{index}</span>
          {duration && (
            <>
              <span className="h-3 w-px bg-white/15" />
              <span className="text-xs text-muted-foreground">{duration}</span>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => onWatch(episodeId)}
          className="text-left hover:underline cursor-pointer"
        >
          <h3 className="display-sm mt-1 truncate text-sm sm:text-base font-semibold">
            {title}
          </h3>
        </button>
        {description && (
          <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-muted-foreground sm:text-sm">
            {description}
          </p>
        )}
      </div>

      <Button
        size="sm"
        variant="outline"
        onClick={() => onWatch(episodeId)}
        className="self-end sm:self-center shrink-0 rounded-full border-white/15 hover:border-white/30 text-xs"
      >
        <Icon icon="ph:play-fill" className="mr-1.5 size-3.5" />
        Play
      </Button>
    </div>
  );
}

export default TitleDetailView;
